import type { AppDataTableColumn } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { AppBadge, AppIconButton, AppTooltip } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import type { SharableOpportunity } from 'backend/types'
import { Search } from 'lucide-react'
import { BASE_PATH } from '@/lib/env'
import { formatDate, toDateTime } from '@/lib/format-utils'

/**
 * テキストを指定文字数で省略
 * dracaenaのtruncateTextフィルタに準拠（デフォルト25文字）
 */
function truncateText(text: string | null, maxLength = 25): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}

/**
 * Tooltipつき省略テキスト
 * dracaenaのtruncateTextフィルタに準拠（デフォルト25文字）
 */
function TruncatedText({ text, maxLength = 25 }: { text: string | null; maxLength?: number }) {
  if (!text) return <span />

  const truncated = truncateText(text, maxLength)
  const isTruncated = text.length > maxLength

  if (!isTruncated) return <span>{text}</span>

  return (
    <AppTooltip content={<p>{text}</p>} className='max-w-md whitespace-pre-wrap'>
      <span className='cursor-help'>{truncated}</span>
    </AppTooltip>
  )
}

/**
 * 公開オポチュニティ一覧のカラム定義
 */
export function createPublicOpportunitiesColumns(): AppDataTableColumn<SharableOpportunity>[] {
  const handlePropertyClick = (row: SharableOpportunity, e: React.MouseEvent) => {
    const url = `${BASE_PATH}/opportunities/public/${row.id}/properties`
    if (e.ctrlKey || e.metaKey) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      window.location.href = url
    }
  }

  return [
    {
      key: 'customer_issue',
      label: '顧客課題',
      accessor: (row) => row.customer_issue,
      render: (_row, value) => <TruncatedText text={value as string | null} />,
      required: true,
      active: true,
    },
    {
      key: 'proposal_summary',
      label: '提案内容',
      accessor: (row) => row.proposal_summary,
      render: (_row, value) => <TruncatedText text={value as string | null} />,
      sortable: false,
      required: true,
      active: true,
    },
    {
      key: 'background',
      label: '背景',
      accessor: (row) => row.background,
      render: (_row, value) => <TruncatedText text={value as string | null} />,
      active: false,
    },
    {
      key: 'description',
      label: '説明・詳細',
      accessor: (row) => row.description,
      render: (_row, value) => <TruncatedText text={value as string | null} />,
      sortable: false,
      active: false,
    },
    {
      key: 'trigger',
      label: 'きっかけ',
      accessor: (row) => row.trigger,
      render: (_row, value) => <TruncatedText text={value as string | null} />,
      active: false,
    },
    {
      key: 'information_route',
      label: '情報源',
      accessor: (row) => row.information_route,
      render: (_row, value) => <span>{value ? String(value) : '-'}</span>,
      active: false,
    },
    {
      key: 'shift_service_name',
      label: 'サービス名',
      accessor: (row) => row.shift_service_name,
      render: (_row, value) => (value ? <AppBadge color='orange'>{String(value)}</AppBadge> : '-'),
      active: true,
    },
    {
      key: 'shift_service_categories_name',
      label: 'カテゴリ名',
      accessor: (row) => row.shift_service_categories_name,
      render: (_row, value) => (value ? <AppBadge color='green'>{String(value)}</AppBadge> : '-'),
      active: true,
    },
    {
      key: 'reason_for_negotiation',
      label: '商談化した理由',
      accessor: (row) => row.reason_for_negotiation,
      render: (_row, value) => <TruncatedText text={value as string | null} />,
      headerClassName: 'w-[100px]',
      cellClassName: 'w-[100px]',
      active: false,
    },
    {
      key: 'updated_at',
      label: '更新日時',
      accessor: (row) => row.updated_at,
      render: (_row, value) => {
        if (!value) return '-'
        return (
          <AppTooltip content={toDateTime(value as number)?.toFormat('yyyy/MM/dd HH:mm:ss') ?? '-'}>
            <span className='cursor-help'>{formatDate(value as number, 'relative')}</span>
          </AppTooltip>
        )
      },
      active: true,
    },
    {
      key: 'action',
      label: '操作',
      accessor: (_row) => null,
      render: (row) => (
        <div className='flex items-center justify-center'>
          <AppIconButton
            onClick={(e: React.MouseEvent) => handlePropertyClick(row, e)}
            description='プロパティを開く（Ctrl+クリックで新窓）'
          >
            <Search className='w-4 h-4' />
          </AppIconButton>
        </div>
      ),
      sortable: false,
      cellClassName: 'text-center',
      headerClassName: 'text-center',
      required: true,
      active: true,
    },
  ]
}
